import { InnerSection, Label, Main, ArrowButton } from './Isometry.Controls.Arrows.style'
import { useState, useMemo, useCallback } from 'react'
import { animate } from 'framer-motion'
import { Arrow } from './Isometry.Controls.Arrows.assets'
import { useBreak } from 'hooks/useBreak'
import { motion } from 'framer-motion'
import { active_project, rotate_by_slider, vis_showOldSlider } from 'state/store.global'
import { getFocus, setFocus, useGet, useSet } from 'state/jotai'
import { _project } from 'state/store.projects'
import { getIsoData, getLowIsoData } from 'state/jotai.isometryDataHooks'
import { _bootstrap } from 'state/store.bootstrap'
import { _isometry } from 'state/store.isometry'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useLocation } from 'react-router-dom'

export const Arrows = ({ frame, setFrame, config, index, setIndex}) => {
  const type = getFocus(_bootstrap, "type");
  const here = useLocation().pathname;
  const isIsometry = here.includes("isometry")
  const [state, setState] = useState(0)
  const [showTooltip, setShowTooltip] = useState(false)
  const isMobile = useBreak('md_dn')

  const SNP = config.IMAGE_SNAPFRAMES
  const safeIndex = index >= 0 ? index : 0

  const MAX = useMemo(() => SNP?.length - 1, [SNP])
  const MIN = 0
  const setRotate_Done = useSet(rotate_by_slider)
  const { pin, pid } = useGet(active_project)
  const buttonsLabel = getFocus(_project, 'buttonsLabel')?.[pin]?.[pid] ?? []


  const lowQualityPercent = getLowIsoData('percent')
  const percent = getIsoData('percent')
  const showOldSlider = useGet(vis_showOldSlider);

  const pids = useGet(_bootstrap).projectids
  const allConfig = getFocus(_isometry, 'config')

  const setFrameFocus = setFocus(_isometry, 'activeframe')
  const getFrame = getFocus(_isometry, 'activeframe')

  const getArrowFill = useCallback((direction) => {
    return pin === 0 && type === "poly" && isIsometry ? (isMobile ? clr.primary_text : "#000") : clr?.primary_text
  }, [pin, type, isIsometry, isMobile, clr])

  const getAnimationConfig = useCallback((DIR) => {
    if (pin === 0) {
      return {
        duration: 0.9,
        transition: { ease: 'ease-in-out' },
      }
    }
    return {
      duration: 0.3,
      transition: { ease: 'linear' },
    }
  }, [pin])

  const click = useCallback((DIR) => {
    if (showOldSlider) {
      if ((safeIndex === MAX && DIR > 0) || (safeIndex === MIN && DIR < 0)) return;
    }

    if (state === 1) return
    
    setRotate_Done(false)
    setState(1)
    let lastFrame = null

    const from = (() => {
      if (safeIndex === MAX && DIR > 0) return SNP?.[MIN]
      if (safeIndex === MIN && DIR < 0) return SNP?.[MAX]
      return SNP?.[safeIndex]
    })()
    
    const to = (() => {
      if (safeIndex === MAX && DIR > 0) return SNP?.[1]
      if (safeIndex === MIN && DIR < 0) return SNP?.[MAX - 1]
      return SNP?.[safeIndex + DIR]
    })()
    
    const toIndex = (() => {
      if (safeIndex === MAX && DIR > 0) { return 1 }
      if (safeIndex === MIN && DIR < 0) { return MAX - 1 }
      return safeIndex + DIR
    })()

    setIndex(toIndex)

    const animationConfig = getAnimationConfig(DIR)
    
    if (pin === 0 && Object.keys(allConfig?.[0]?.ANGLES || {}).length) {
      animate(from, to, {
        ...animationConfig,
        onUpdate: v => {
          const nextFrame = Math.round(v)
          if (nextFrame === lastFrame) return
          lastFrame = nextFrame
          if (nextFrame === SNP[toIndex]) setRotate_Done(true)
          setFrameFocus((f) => (pids.map((p, cnfIndex) => {
            const overviewPid = allConfig?.[0]?.[pids[0]]?.ANGLES;
            if (cnfIndex === 0) {
              return {
                [p]: nextFrame
              }
            } else {
              return {
                [p]: overviewPid?.[toIndex]?.[p]?.[p]
              }
            }
          })))
        },
        onComplete: () => setState(0)
      })
    } else {
      animate(from, to, {
        ...animationConfig,
        onUpdate: (v) => {
          const nextFrame = Math.round(v)
          if (nextFrame === lastFrame) return
          lastFrame = nextFrame
          if (nextFrame === SNP[toIndex]) setRotate_Done(true)
          setFrame(nextFrame)
        },
        onComplete: () => setState(0)
      })
    }
  }, [showOldSlider, index, MAX, MIN, state, setRotate_Done, SNP, setIndex, getAnimationConfig, pin, pids, allConfig, setFrameFocus, setFrame])

  const animation = useMemo(() => ({
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2, ease: 'linear' },
  }), [])

  const leftDisabled = useMemo(() => showOldSlider && safeIndex === MIN, [showOldSlider, safeIndex, MIN])
  const rightDisabled = useMemo(() => showOldSlider && safeIndex === MAX, [showOldSlider, safeIndex, MAX])
  
  const props = useMemo(() => ({ 
    type, 
    isIsometry, 
    pin, 
    isMobile, 
    percent: lowQualityPercent,
    clr 
  }), [type, isIsometry, pin, isMobile, lowQualityPercent, clr])

  return (
    <Main as={motion.div} {...animation}>

      {!isMobile && <Tooltip showTooltip={showTooltip} />}
      <ArrowButton
        variant="primary"
        {...props}
        onClick={() => click(-1)}
        onMouseEnter={() => !leftDisabled && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={leftDisabled}
      >
        <InnerSection disabled={leftDisabled}>
          <Arrow 
            {...props} 
            D="L" 
            w={isMobile ? '25' : '25'} 
            fill={getArrowFill('left')} 
            disabled={leftDisabled} 
          />
        </InnerSection>
      </ArrowButton>
      <ArrowButton
        variant="primary"
        {...props}
        onClick={() => click(+1)}
        onMouseEnter={() => !rightDisabled && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={rightDisabled}
      >
        <InnerSection disabled={rightDisabled}>
          <Arrow 
            {...props} 
            D="R" 
            w={isMobile ? '25' : '25'} 
            fill={getArrowFill('right')} 
            disabled={rightDisabled} 
          />
        </InnerSection>
      </ArrowButton>
    </Main>
  )
}